import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import {
    resetForm,
    getCompanies,
    getXeroConfigsForCompany,
    // getCustomers,
    // getAccountCodes
} from './store/dataSlice'
import reducer from './store'
import { injectReducer } from 'store'
import { Form, Formik } from 'formik'
import { Success } from 'utils/alerts'
import { Tabs } from 'components/Ui'
import LineItems from './LineItems'
import InvoiceHeader from './InvoiceHeader'
import AttachDocuments from './AttachDocuments'
// Removed unused import: selectCurrentCompany

// Import modular components and utilities
import { LoadingSpinner, NoCompaniesFound, NoXeroConfig } from './components/LoadingStates'
import PageHeader from './components/PageHeader'
import FormActions from './components/FormActions'
import { useInvoiceData } from './hooks/useInvoiceData'
import { useInvoiceSubmission } from './hooks/useInvoiceSubmission'
import { validationSchema } from './utils/validationSchema'
import { getInitialValues } from './utils/formTransformers'
import { handleLineItemAdd, handleLineItemRemove, handleLineItemUpdate } from './utils/lineItemHandlers'
import { handleSyncAllData, handleRefreshData } from './utils/syncHandlers'

injectReducer('xero', reducer)

const Create = () => {
    const { id } = useParams()
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [syncing, setSyncing] = useState(false)

    // Use custom hooks for data management and form submission
    const {
        recordData,
        loading,
        companies,
        selectedCompany,
        xeroConfigs,
        xeroConfig,
        customers,
        accountCodes,
        taxTypes,
        handleConfigChange,
    } = useInvoiceData(id)

    // Use selectedCompany from the hook (which now handles company admin logic internally)
    const effectiveCompany = selectedCompany

    // Handle form discard/navigation
    const handleDiscard = () => {
        dispatch(resetForm())
        navigate('/finacials/xero')
    }

    const { submitting, handleSubmit } = useInvoiceSubmission(
        id,
        recordData,
        effectiveCompany,
        effectiveCompany, // Use effectiveCompany instead of globalCompany
        xeroConfig,
        customers,
        handleDiscard
    )

    // Sync handlers
    const handleSyncData = () => handleSyncAllData(xeroConfig, effectiveCompany, setSyncing, dispatch)
    const handleRefreshDataAction = () => handleRefreshData(effectiveCompany, setSyncing, dispatch)

    const handleRefreshConfig = () => {
        console.log('Refreshing company data...')
        dispatch(getCompanies())
        if (effectiveCompany) {
            dispatch(getXeroConfigsForCompany(effectiveCompany.id))
        }
        Success('Company data refreshed')
    }

    // Get initial values using the modular utility
    const initialValues = getInitialValues(id, recordData, customers, accountCodes)

    // Handle loading and error states
    if (loading) {
        return (
            <LoadingSpinner
                loading={loading}
                customers={customers}
                accountCodes={accountCodes}
                globalCompany={effectiveCompany}
                message="Loading invoice data..."
            />
        )
    }

    if (!companies || companies.length === 0) {
        return <NoCompaniesFound />
    }

    if (!effectiveCompany) {
        return (
            <LoadingSpinner
                loading={false}
                customers={customers}
                accountCodes={accountCodes}
                globalCompany={effectiveCompany}
                message="No company available. Please contact your administrator."
            />
        )
    }

    if (!xeroConfigs && !xeroConfig && !(id && recordData && recordData.configuration_id)) {
        return <NoXeroConfig companyName={effectiveCompany?.name} />
    }

    return (
        <>
            <PageHeader
                title={id ? 'Edit Invoice' : 'Create Invoice'}
                selectedCompany={effectiveCompany}
                globalCompany={effectiveCompany} // Use effectiveCompany for consistency
                xeroConfigs={xeroConfigs}
                xeroConfig={xeroConfig}
                recordData={recordData}
                id={id}
                onConfigChange={handleConfigChange}
                onSyncData={handleSyncData}
                onRefreshConfig={handleRefreshConfig}
                onBack={handleDiscard}
            />

            <div className="flex flex-col items-center">
                <div className="w-full bg-white border rounded-sm border-stroke shadow-default dark:border-strokedark dark:bg-boxdark">
                    <div className="px-5 py-3 border-b border-stroke dark:border-strokedark">
                        <h3 className="font-medium text-black dark:text-white">{id ? 'Update Invoice' : 'Create New Invoice'}</h3>
                    </div>

                    <div className="flex flex-col gap-3 p-6">
                        <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            enableReinitialize={true} // Force re-initialization when data changes
                            onSubmit={handleSubmit}
                            // Debug initialValues
                            initialStatus={initialValues => {
                                console.log('Formik initialValues:', initialValues)
                                return {}
                            }}
                        >
                            {({ values, setFieldValue, touched, errors, isValid }) => (
                                <Form>
                                    <Tabs.Group version={1}>
                                        <Tabs.List>
                                            <Tabs>Invoice Header</Tabs>
                                            <Tabs>Line Items</Tabs>
                                            <Tabs>Attach Documents</Tabs>
                                        </Tabs.List>

                                        <Tabs.Panels className="mt-4">
                                            {/* Invoice Header Tab */}
                                            <Tabs.Panel>
                                                <InvoiceHeader customers={customers} id={id} />
                                            </Tabs.Panel>

                                            {/* Line Items Tab */}
                                            <Tabs.Panel>
                                                <LineItems
                                                    values={values}
                                                    setFieldValue={setFieldValue}
                                                    errors={errors}
                                                    touched={touched}
                                                    accountCodes={accountCodes}
                                                    taxTypes={taxTypes}
                                                    handleLineItemAdd={handleLineItemAdd}
                                                    handleLineItemRemove={handleLineItemRemove}
                                                    handleLineItemUpdate={handleLineItemUpdate}
                                                />
                                            </Tabs.Panel>

                                            {/* Attach Documents Tab */}
                                            <Tabs.Panel>
                                                <AttachDocuments recordData={recordData} xeroConfig={xeroConfig} />
                                            </Tabs.Panel>
                                        </Tabs.Panels>
                                    </Tabs.Group>

                                    <FormActions
                                        id={id}
                                        submitting={submitting}
                                        syncing={syncing}
                                        isValid={isValid}
                                        selectedCompany={effectiveCompany}
                                        handleRefreshData={handleRefreshDataAction}
                                        handleDiscard={handleDiscard}
                                    />
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Create
